/**
 * SlideFullPreview.tsx
 *
 * Renders a realistic 16:9 HTML/CSS preview of a slide with actual text
 * content positioned where it would appear in the final PPTX, and any
 * generated images shown in their layout positions.
 *
 * Used by `SlideLightbox` for the "Ver macro" right-click action.
 */

import type { SlideConfig } from '@/types/wizardV2';
import styles from './SlideFullPreview.module.css';

interface Props {
  slide: SlideConfig;
  plantillaBgUrl?: string;
  slideNumber?: number;
  /** Generated image URL for the primary image slot (slot 0). */
  imageUrl?: string | null;
  /** All image URLs for multi-image components (indexed by image slot). */
  imageUrls?: (string | null)[];
}

function blockText(block: Record<string, unknown>): string {
  return String(
    block.text ?? block.label ?? block.value ?? block.title ?? block.name ?? '',
  );
}

function blockSub(block: Record<string, unknown>): string | undefined {
  const v = block.subtitle ?? block.description ?? block.time ?? block.year;
  return v != null ? String(v) : undefined;
}

function imgElForUrl(url: string | null | undefined, s: Record<string, string>) {
  return url ? (
    <img src={url} alt="" className={s.img} draggable={false} />
  ) : (
    <div className={s.imgPlaceholder} />
  );
}

function renderContent(
  slide: SlideConfig,
  imageUrl: string | null | undefined,
  s: Record<string, string>,
  imageUrls?: (string | null)[],
): React.ReactNode {
  const { component, title, subtitle, blocks = [] } = slide;

  const imgEl = imgElForUrl(imageUrl, s);

  switch (component) {
    case 'hero_cover':
      return (
        <div className={s.centered}>
          {title && <h2 className={s.heroTitle}>{title}</h2>}
          {subtitle && <p className={s.heroSubtitle}>{subtitle}</p>}
        </div>
      );

    case 'hero_section':
    case 'hero_closing':
      return (
        <div className={s.centered}>
          <h2 className={s.sectionTitle}>{title || slide.section}</h2>
        </div>
      );

    case 'hero_stats':
      return (
        <div className={s.statsRow}>
          {blocks.slice(0, 4).map((b, i) => (
            <div key={i} className={s.statBox}>
              <span className={s.statValue}>{blockText(b)}</span>
              <span className={s.statLabel}>{blockSub(b) ?? ''}</span>
            </div>
          ))}
        </div>
      );

    case 'hero_client_logos':
      return (
        <div className={s.logosRow}>
          {blocks.slice(0, 5).map((b, i) => (
            <div key={i} className={s.logoBox}>{blockText(b) || `Logo ${i + 1}`}</div>
          ))}
        </div>
      );

    case 'concepto_full':
      return (
        <div className={s.textFull}>
          {title && <h2 className={s.contentTitle}>{title}</h2>}
          {subtitle && <p className={s.contentSubtitle}>{subtitle}</p>}
          <div className={s.bulletList}>
            {blocks.map((b, i) => (
              <p key={i} className={s.bulletItem}>• {blockText(b)}</p>
            ))}
          </div>
        </div>
      );

    case 'content_image_right':
    case 'product_showcase_right':
    case 'showcase_image_right_round':
      return (
        <div className={s.splitRight}>
          <div className={s.splitText}>
            {title && <h2 className={s.contentTitle}>{title}</h2>}
            {subtitle && <p className={s.contentSubtitle}>{subtitle}</p>}
            <div className={s.bulletList}>
              {blocks.map((b, i) => (
                <p key={i} className={s.bulletItem}>• {blockText(b)}</p>
              ))}
            </div>
          </div>
          <div className={`${s.splitImage} ${component === 'showcase_image_right_round' ? s.roundImage : ''}`}>
            {imgEl}
          </div>
        </div>
      );

    case 'content_image_left':
    case 'product_showcase_left':
    case 'showcase_image_left_round':
      return (
        <div className={s.splitLeft}>
          <div className={`${s.splitImage} ${component === 'showcase_image_left_round' ? s.roundImage : ''}`}>
            {imgEl}
          </div>
          <div className={s.splitText}>
            {title && <h2 className={s.contentTitle}>{title}</h2>}
            {subtitle && <p className={s.contentSubtitle}>{subtitle}</p>}
            <div className={s.bulletList}>
              {blocks.map((b, i) => (
                <p key={i} className={s.bulletItem}>• {blockText(b)}</p>
              ))}
            </div>
          </div>
        </div>
      );

    case 'product_showcase_pair':
      return (
        <div className={s.pairRow}>
          <div className={s.pairItem}>
            {imgElForUrl(imageUrls?.[0] ?? imageUrl, s)}
            {blocks[0] && <span className={s.pairCaption}>{blockText(blocks[0])}</span>}
          </div>
          <div className={s.pairItem}>
            {imgElForUrl(imageUrls?.[1] ?? null, s)}
            {blocks[1] && <span className={s.pairCaption}>{blockText(blocks[1])}</span>}
          </div>
        </div>
      );

    case 'product_grid_2':
    case 'product_grid_3':
      return (
        <div className={s.gridRow}>
          {blocks.slice(0, component === 'product_grid_3' ? 3 : 2).map((b, i) => (
            <div key={i} className={s.gridItem}>
              {imgElForUrl(imageUrls?.[i] ?? null, s)}
              <span className={s.gridCaption}>{blockText(b)}</span>
            </div>
          ))}
        </div>
      );

    case 'data_timeline':
      return (
        <div className={s.timeline}>
          {title && <h2 className={s.contentTitle}>{title}</h2>}
          <div className={s.timelineList}>
            {blocks.map((b, i) => {
              const sub = blockSub(b);
              return (
                <div key={i} className={s.timelineEntry}>
                  <span className={s.timelineDot} />
                  <div className={s.timelineContent}>
                    {sub && <span className={s.timelineTime}>{sub}</span>}
                    <span className={s.timelineText}>{blockText(b)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );

    case 'data_cost_table':
      return (
        <div className={s.costTable}>
          {title && <h2 className={s.contentTitle}>{title}</h2>}
          <div className={s.tableGrid}>
            {blocks.map((b, i) => (
              <div key={i} className={i === 0 ? s.tableHeaderRow : s.tableRow}>
                <span>{blockText(b)}</span>
                {blockSub(b) && <span className={s.tableValue}>{blockSub(b)}</span>}
              </div>
            ))}
          </div>
        </div>
      );

    case 'journey_overview':
      return (
        <div className={s.journeyRow}>
          {title && <h2 className={s.contentTitle}>{title}</h2>}
          <div className={s.journeySteps}>
            {blocks.slice(0, 5).map((b, i) => (
              <div key={i} className={s.journeyStep}>
                <span className={s.journeyNum}>{i + 1}</span>
                <span className={s.journeyLabel}>{blockText(b)}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case 'showcase_full_bleed':
    case 'showcase_venue':
    case 'zona_instagrameable_showcase':
    case 'render_showcase':
      return <div className={s.fullBleed}>{imgEl}</div>;

    case 'invitation_showcase':
      return (
        <div className={s.pairRow}>
          <div className={s.pairItem}>
            {imgElForUrl(imageUrls?.[0] ?? imageUrl, s)}
          </div>
          <div className={s.pairItem}>
            {imgElForUrl(imageUrls?.[1] ?? null, s)}
          </div>
        </div>
      );

    case 'people_composite':
      return (
        <div className={s.peopleRow}>
          {blocks.slice(0, 3).map((b, i) => (
            <div key={i} className={s.personCard}>
              <div className={s.personAvatar} />
              <span className={s.personName}>{blockText(b)}</span>
              {blockSub(b) && <span className={s.personRole}>{blockSub(b)}</span>}
            </div>
          ))}
        </div>
      );

    case 'people_speaker_bio':
      return (
        <div className={s.speakerBio}>
          <div className={s.speakerText}>
            {title && <h2 className={s.contentTitle}>{title}</h2>}
            {subtitle && <p className={s.contentSubtitle}>{subtitle}</p>}
            <div className={s.bulletList}>
              {blocks.map((b, i) => (
                <p key={i} className={s.bulletItem}>• {blockText(b)}</p>
              ))}
            </div>
          </div>
          <div className={`${s.splitImage} ${s.roundImage}`}>
            {imgEl}
          </div>
        </div>
      );

    default:
      return (
        <div className={s.splitRight}>
          <div className={s.splitText}>
            {title && <h2 className={s.contentTitle}>{title}</h2>}
            {subtitle && <p className={s.contentSubtitle}>{subtitle}</p>}
            <div className={s.bulletList}>
              {blocks.map((b, i) => (
                <p key={i} className={s.bulletItem}>• {blockText(b)}</p>
              ))}
            </div>
          </div>
          <div className={s.splitImage}>{imgEl}</div>
        </div>
      );
  }
}

export const SlideFullPreview = ({ slide, plantillaBgUrl, slideNumber, imageUrl, imageUrls }: Props) => {
  return (
    <div className={styles.slide}>
      {plantillaBgUrl && (
        <div
          className={styles.bgImage}
          style={{ backgroundImage: `url(${plantillaBgUrl})` }}
          aria-hidden
        />
      )}
      <div className={styles.overlay} />
      <div className={styles.content}>
        {renderContent(slide, imageUrl, styles, imageUrls)}
      </div>
      {slideNumber !== undefined && (
        <span className={styles.slideNumber}>{slideNumber}</span>
      )}
    </div>
  );
};
